import { useCallback, useEffect, useState } from "react";
import { getMobilePlatform } from "@/utils/device";

/**
 * "Add to home screen" for a genie's own page. Android's browser hands us a
 * one-shot install prompt (`beforeinstallprompt`) we save and replay on tap;
 * iOS has no such API, so the caller shows the Share → Add to Home Screen
 * steps instead. Desktop never sees the item.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export type AddToHomeScreenMode = "android" | "ios" | "hidden";

// Module-level so a prompt fired before the More menu mounts is not lost.
let savedPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
let listening = false;

const capturePrompt = (event: Event) => {
  // Stop the browser's own mini-infobar; we offer it from More actions.
  event.preventDefault();
  savedPrompt = event as BeforeInstallPromptEvent;
  listeners.forEach((notify) => notify());
};

const clearPrompt = () => {
  savedPrompt = null;
  listeners.forEach((notify) => notify());
};

const ensureListening = () => {
  if (listening || typeof window === "undefined") return;
  listening = true;
  window.addEventListener("beforeinstallprompt", capturePrompt);
  window.addEventListener("appinstalled", clearPrompt);
};

ensureListening();

/** Test-only: forget any saved prompt between cases. */
export const resetAddToHomeScreenForTests = () => {
  savedPrompt = null;
};

const isStandalone = () => {
  if (typeof window === "undefined") return false;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true || !!window.matchMedia?.("(display-mode: standalone)").matches;
};

const resolveMode = (): AddToHomeScreenMode => {
  if (isStandalone()) return "hidden";
  const platform = getMobilePlatform();
  return platform === "other" ? "hidden" : platform;
};

export const useAddToHomeScreen = () => {
  const [mode] = useState<AddToHomeScreenMode>(resolveMode);
  const [hasPrompt, setHasPrompt] = useState(() => savedPrompt !== null);
  const [instructionsOpen, setInstructionsOpen] = useState(false);

  useEffect(() => {
    ensureListening();
    const notify = () => setHasPrompt(savedPrompt !== null);
    listeners.add(notify);
    notify();
    return () => {
      listeners.delete(notify);
    };
  }, []);

  const trigger = useCallback(async () => {
    if (mode === "android" && savedPrompt) {
      const event = savedPrompt;
      // A prompt can only be shown once; the browser fires a fresh one later.
      clearPrompt();
      try {
        await event.prompt();
        return;
      } catch {
        // The browser refused the saved prompt; fall through to the manual steps.
      }
    }
    // iOS, or Android without a prompt (already dismissed, or a browser that
    // never fires one): show the manual steps.
    setInstructionsOpen(true);
  }, [mode]);

  return {
    mode,
    isVisible: mode !== "hidden",
    hasPrompt,
    trigger,
    instructionsOpen,
    closeInstructions: useCallback(() => setInstructionsOpen(false), []),
  };
};
